import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getCrmPipeline, updateCrmStage } from '../services/api';
import './Pipeline.css';

const PIPELINES = {
  club:   { label:'Club Pipeline', stages: [
    { id:'lead',           label:'Lead verified',    color:'#646B6B' },
    { id:'outreach',       label:'Outreach sent',    color:'#4fc3f7' },
    { id:'engaged',        label:'Engaged',          color:'#f5a623' },
    { id:'demo_scheduled', label:'Demo scheduled',   color:'#a78bfa' },
    { id:'onboarding',     label:'Onboarding',       color:'#ff9f43' },
    { id:'onboarded',      label:'Onboarded',        color:'#A7F432' },
    { id:'activated',      label:'Activated ⭐',     color:'#A7F432' },
  ]},
  scout:  { label:'Scout Pipeline', stages: [
    { id:'lead',           label:'Lead verified',    color:'#646B6B' },
    { id:'outreach',       label:'Outreach sent',    color:'#4fc3f7' },
    { id:'engaged',        label:'Engaged',          color:'#f5a623' },
    { id:'app_downloaded', label:'App downloaded',   color:'#a78bfa' },
    { id:'profile_created',label:'Profile created',  color:'#ff9f43' },
    { id:'club_connected', label:'Club connected',   color:'#4fc3f7' },
    { id:'activated',      label:'Activated ⭐',     color:'#A7F432' },
  ]},
  player: { label:'Player Pipeline', stages: [
    { id:'lead',                 label:'Lead',              color:'#646B6B' },
    { id:'outreach',             label:'Outreach',          color:'#4fc3f7' },
    { id:'registered',           label:'Registered',        color:'#f5a623' },
    { id:'profile_complete',     label:'Profile complete',  color:'#a78bfa' },
    { id:'evaluation_purchased', label:'Eval purchased ⭐', color:'#A7F432' },
    { id:'activated',            label:'Activated',         color:'#A7F432' },
  ]},
};

function ContactCard({ contact, pipelineType, onMove, stages }) {
  const name = contact.clubName || contact.name || `${contact.firstName||''} ${contact.lastName||''}`.trim();
  const email = contact.contactEmail || contact.email;

  return (
    <div style={{
      background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10,
      padding:'12px 14px', marginBottom:8, cursor:'default',
    }}>
      <div style={{ fontWeight:500, fontSize:13, color:'var(--text-primary)', marginBottom:2 }}>{name}</div>
      <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:8 }}>{email}</div>
      {contact.affiliatedClub && <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:6 }}>↳ {contact.affiliatedClub}</div>}
      {contact.evaluationsCompleted > 0 && (
        <div style={{ fontSize:11, color:'var(--accent)', marginBottom:6 }}>{contact.evaluationsCompleted} evaluations</div>
      )}
      <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:8 }}>
        Joined {new Date(contact.createdAt).toLocaleDateString()}
      </div>
      <select
        value={contact.pipelineStage || 'lead'}
        onChange={e => onMove(contact, e.target.value)}
        onClick={e => e.stopPropagation()}
        style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-muted)', fontSize:11, padding:'4px 8px', cursor:'pointer' }}
      >
        {stages.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
    </div>
  );
}

export default function Pipeline({ toast }) {
  const { token } = useApp();
  const [pipelineType, setPipelineType] = useState('club');
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (type) => {
    setLoading(true); setError(null);
    try {
      const res = await getCrmPipeline(token, type);
      setData(res.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(pipelineType); }, [pipelineType, token]);

  const handleMove = async (contact, stage) => {
    try {
      const type = pipelineType === 'club' ? 'club' : 'user';
      await updateCrmStage(token, contact.id, type, stage);
      toast?.(`Moved to ${stage.replace(/_/g,' ')}`);
      load(pipelineType);
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const cfg    = PIPELINES[pipelineType];
  const stages = cfg.stages;
  const grouped = data?.grouped || {};
  const total   = data?.total ?? 0;

  return (
    <div>
      {/* Pipeline selector */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {Object.entries(PIPELINES).map(([key, p]) => (
          <button key={key} className={`btn ${pipelineType===key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPipelineType(key)}>
            {p.label}
          </button>
        ))}
      </div>

      {error && <div className="card" style={{ padding:16, marginBottom:16, borderColor:'var(--danger)' }}><p style={{ color:'var(--danger)', fontSize:13 }}>{error}</p></div>}

      {loading ? (
        <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading pipeline…</div>
      ) : (
        <>
          <div style={{ marginBottom:16, fontSize:13, color:'var(--text-muted)' }}>
            {total} total contacts in the {cfg.label.toLowerCase()}
          </div>

          {/* Kanban board */}
          <div style={{ display:'flex', gap:12, overflowX:'auto', paddingBottom:16, minHeight:400, alignItems:'flex-start' }}>
            {stages.map(stage => {
              const cards = grouped[stage.id] || [];
              return (
                <div key={stage.id} style={{ minWidth:220, maxWidth:240, flexShrink:0 }}>
                  {/* Column header */}
                  <div style={{
                    background:'var(--bg-card)', border:'1px solid var(--border)',
                    borderRadius:10, padding:'10px 14px', marginBottom:8,
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                  }}>
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)' }}>{stage.label}</span>
                    <span style={{
                      background: `${stage.color}22`, color: stage.color,
                      borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:700,
                    }}>{cards.length}</span>
                  </div>

                  {/* Cards */}
                  <div style={{ minHeight:100 }}>
                    {cards.map(c => (
                      <ContactCard
                        key={c.id}
                        contact={c}
                        pipelineType={pipelineType}
                        stages={stages}
                        onMove={handleMove}
                      />
                    ))}
                    {cards.length === 0 && (
                      <div style={{ padding:'20px 0', textAlign:'center', color:'var(--text-dim)', fontSize:12, border:'1px dashed var(--border)', borderRadius:8 }}>
                        Empty
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
